<template>
    <div>
        <div class="is-clearfix">
            <div class="chart-title is-pulled-left">
                <h1 class="title">{{title}}</h1>
                <h2 class="subtitle">{{subtitle}}</h2>
            </div>
            <button class="is-pulled-right button is-small" @click="exportPPTX" :disabled="isExporting || !project.metrics.length">
                <b-icon icon="file-powerpoint"
                        size="is-small"
                        pack="far">
                </b-icon>
                <span>Export</span>
            </button>
        </div>
        <hr>
        <chart class="chart-content" :chartData="chartData"/>
        <chart-legend :legends="chartData.settings.series"/>
        <div style="clear: both"></div>
    </div>
</template>

<script>
import Chart from '@/components/charts/Chart.vue';
import ChartLegend from '@/components/charts/ChartLegend.vue';

import _ from 'lodash';

import { exporterService } from '@/services/exporter-service';
import { finalScoreProgressRequestor } from '@/requestors/final-score-progress-requestor.js';
import { chartTypes } from '@/components/charts/common/chartTypes';
import { chartLabelGeneratorService } from '@/services/chart-label-generator-service';
import { progressChartHelperService } from '@/services/progress-chart-helper-service';

export default {
    name: 'FinalScoreProgress',
    components: {
        Chart,
        ChartLegend
    },
    props: {
        project: {
            type: Object,
            required: true
        }
    },
    data () {
        return {
            title: 'Conversability Score Trend',
            subtitle: '',
            chartData: {
                chartType: chartTypes.ORDINALLINE,
                settings: {
                    xAxis: {
                        label: 'Date',
                        noOverlap: true
                    },
                    yAxis: {
                        minValue: 0,
                        label: 'Conversability Score',
                        ticks: 5
                    },
                    series: []
                },
                meta: {
                    metric: ['periodName', 'finalScore'],
                    series: 'columnId'
                },
                data: []
            },
            isExporting: false
        };
    },
    watch: {
        'project.metrics': {
            handler: function (newMetrics) {
                this.chartData.data = newMetrics;
                const latestMetric = _.maxBy(newMetrics, m => m.day);
                const earliestMetric = _.minBy(newMetrics, m => m.day);
                if (earliestMetric && latestMetric) {
                    let strPattern = chartLabelGeneratorService.generateProgressChartLabelPattern(this.project.meta.period, this.project.meta.daySmoothing);
                    this.subtitle = chartLabelGeneratorService.formatString(strPattern, earliestMetric.periodStartDate, latestMetric.periodEndDate, this.project.meta.daySmoothing);
                }
                this.chartData.settings.xAxis.label = progressChartHelperService.buildXAxisTitle(this.project.meta.period, this.project.meta.daySmoothing);
            },
            deep: true
        },
        'project.settings.columns': {
            handler: function (newColumns) {
                this.chartData.settings.series = _(newColumns)
                    .filter(column => column.enabled)
                    .map(column => ({
                        id: column.id,
                        name: column.name,
                        colour: column.colour,
                        visible: true
                    }))
                    .value();
            },
            deep: true
        }
    },
    methods: {
        async exportPPTX () {
            this.isExporting = true;
            let pptxRequest = finalScoreProgressRequestor.generatePPTXRequest(this.chartData.data, this.title, this.subtitle, this.chartData.settings);

            try {
                await exporterService.exportPPTX(pptxRequest);
            } catch (error) {
                this.$buefy.toast.open({
                    message: 'Failed to export ' + this.title + ' Chart',
                    type: 'is-danger'
                });
            } finally {
                this.isExporting = false;
            }
        }
    },
    mounted () {
        let chartDetail = {
            getSlideRequest: () => {
                return finalScoreProgressRequestor.getSlideRequest(this.chartData.data, this.title, this.subtitle, this.chartData.settings);
            }
        };
        this.$emit('on-chart-mount-complete', chartDetail);
    }
};

</script>

<style lang="scss" scoped>
    .chart-content {
        width: 100%;
        height: 300px;
    }
    .title {
        font-size: 16px;
    }
    .subtitle {
        font-size: 14px;
        margin-bottom: 5px;
    }
    hr {
        margin: 5px;
    }
</style>
